// medical-devices-database.js
export const medicalDevicesDatabase = {
    categories: [
      {
        id: "robotic-surgical",
        name: "Robotic Surgical Systems",
        description: "Robotic-assisted surgical platforms for minimally invasive procedures",
        devices: [
          {
            id: "davinci",
            name: "da Vinci Surgical System",
            manufacturer: "Intuitive Surgical",
            description: "Robotic-assisted surgical platform with HD visualization and wristed instruments",
            applications: ["Urology", "Gynecology", "General surgery", "Thoracic surgery"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: {
              spoofing: 4,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "davinci-5",
            name: "da Vinci 5 Surgical System",
            manufacturer: "Intuitive Surgical",
            description: "Fifth-generation robotic surgical platform with force feedback, 10,000x increased computing power, in-console video replay, and remote software updates via Network CCM (FDA cleared March 2024, CE marked July 2025)",
            applications: ["Urology", "Gynecology", "General surgery", "Thoracic surgery", "Pediatric surgery"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: {
              spoofing: 4,
              tampering: 4,
              repudiation: 4,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 4 // Higher than dV Xi due to remote software update capability (Network CCM)
            }
          },
          {
            id: "hugo",
            name: "Hugo RAS System",
            manufacturer: "Medtronic",
            description: "Robotic-assisted surgical system with flexible platform design",
            applications: ["General surgery", "Urology", "Gynecology"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: {
              spoofing: 4,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "mako",
            name: "Mako SmartRobotics",
            manufacturer: "Stryker",
            description: "Robotic arm-assisted surgery platform",
            applications: ["Knee replacements", "Hip replacements"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: {
              spoofing: 3,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "rosa",
            name: "ROSA Robotics System",
            manufacturer: "Zimmer Biomet",
            description: "Robotic surgical assistant for orthopedic procedures",
            applications: ["Knee surgeries", "Hip surgeries", "Shoulder surgeries", "Spine surgeries"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: {
              spoofing: 3,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "cori-surgical",
            name: "CORI Surgical System",
            manufacturer: "Smith & Nephew",
            description: "Handheld robotics platform",
            applications: ["Knee replacements"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Estimated based on Mako/ROSA
              spoofing: 3,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "versius-surgical",
            name: "Versius Surgical Robot",
            manufacturer: "CMR Surgical",
            description: "Modular, portable robotic surgery system",
            applications: ["General surgery", "Gynecology"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Estimated based on da Vinci/Hugo
              spoofing: 4,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
           {
            id: "excelsius-gps",
            name: "ExcelsiusGPS",
            manufacturer: "Globus Medical",
            description: "Robotic navigation platform for spine surgery",
            applications: ["Spinal surgeries"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Estimated based on Mako/ROSA
              spoofing: 3,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "senhance-surgical",
            name: "Senhance Surgical System",
            manufacturer: "Asensus Surgical",
            description: "Digital laparoscopy platform with haptic feedback",
            applications: ["General surgery", "Gynecology", "Urology"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Estimated based on da Vinci/Hugo
              spoofing: 4,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "aquabeam-robotic",
            name: "AquaBeam Robotic System",
            manufacturer: "PROCEPT BioRobotics",
            description: "Water jet ablation system",
            applications: ["Prostate surgery"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Estimated based on da Vinci/Hugo (Urology focus)
              spoofing: 4,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
           {
            id: "genesis-rmn",
            name: "Genesis RMN",
            manufacturer: "Stereotaxis",
            description: "Magnetic navigation system for cardiac procedures",
            applications: ["Cardiac ablation", "Electrophysiology"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Estimated based on other high-risk connected systems
              spoofing: 4,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "robotic-instrument",
            name: "Robotic Instrument",
            manufacturer: "Various (Intuitive Surgical, Medtronic, Stryker, etc.)", // Specific to system
            description: "Specialized tools manipulated by robotic surgical systems",
            applications: ["Surgical procedures"],
            networkConnected: false, // Instruments themselves aren't typically networked
            ephiRiskLevel: "low",
            threatProfiles: { // Minimal direct digital threat
              spoofing: 1,
              tampering: 2, // Physical/sterilization integrity concern
              repudiation: 1,
              informationDisclosure: 1,
              denialOfService: 1, // Physical failure
              elevationOfPrivilege: 1
            }
          }
          // Add more devices as needed
        ]
      },
      {
        id: "diagnostic-imaging",
        name: "Diagnostic Imaging Equipment",
        description: "Medical devices that create visual representations of the interior of the body",
        devices: [
          {
            id: "mri",
            name: "MRI Scanner",
            manufacturer: "Various (Siemens, GE, Philips, Canon)",
            description: "Uses magnetic fields and radio waves to create detailed images",
            applications: ["Neurological imaging", "Musculoskeletal imaging", "Cancer detection"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: {
              spoofing: 3,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "ct-scanner",
            name: "CT Scanner",
            manufacturer: "Various (GE, Siemens, Philips, Canon)",
            description: "Uses X-rays to create cross-sectional images",
            applications: ["Trauma imaging", "Cancer detection", "Vascular imaging"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: {
              spoofing: 3,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "xray",
            name: "X-ray System",
            manufacturer: "Various (Carestream, Siemens, GE, Philips)",
            description: "Radiographic imaging using ionizing radiation",
            applications: ["Bone imaging", "Chest imaging", "Dental imaging"],
            networkConnected: true,
            ephiRiskLevel: "medium",
            threatProfiles: {
              spoofing: 2,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 3,
              elevationOfPrivilege: 2
            }
          },
          {
            id: "ultrasound",
            name: "Ultrasound System",
            manufacturer: "Various (GE Healthcare, Philips, Samsung Medison, Mindray)",
            description: "Uses sound waves to create images",
            applications: ["General imaging", "Obstetrics", "Cardiology"],
            networkConnected: true,
            ephiRiskLevel: "medium",
            threatProfiles: { // Estimated based on X-ray
              spoofing: 2,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 3,
              elevationOfPrivilege: 2
            }
           },
           {
            id: "pet-scanner",
            name: "PET Scanner",
            manufacturer: "Various (Siemens Healthineers, GE Healthcare, Philips)",
            description: "Nuclear medicine functional imaging",
            applications: ["Oncology", "Neurology", "Cardiology"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Estimated based on MRI/CT
              spoofing: 3,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "mammography",
            name: "Mammography System",
            manufacturer: "Various (Hologic, GE Healthcare, Siemens Healthineers)",
            description: "Specialized X-ray imaging for breast examinations",
            applications: ["Breast cancer screening", "Diagnostic imaging"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Estimated based on MRI/CT (sensitive data)
              spoofing: 3,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
           },
           {
            id: "fluoroscopy",
            name: "Fluoroscopy System",
            manufacturer: "Various (Siemens Healthineers, GE Healthcare, Philips)",
            description: "Real-time X-ray imaging",
            applications: ["Interventional radiology", "Orthopedics", "Gastroenterology"],
            networkConnected: true,
            ephiRiskLevel: "medium",
            threatProfiles: { // Estimated based on X-ray
              spoofing: 2,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 3,
              elevationOfPrivilege: 2
            }
           },
           {
             id: "bone-densitometry",
             name: "Bone Densitometry (DEXA)",
             manufacturer: "Various (Hologic, GE Healthcare, Diagnostic Medical Systems)",
             description: "Measures bone mineral density",
             applications: ["Osteoporosis diagnosis", "Fracture risk assessment"],
             networkConnected: true,
             ephiRiskLevel: "medium",
             threatProfiles: { // Estimated based on X-ray
               spoofing: 2,
               tampering: 3,
               repudiation: 3,
               informationDisclosure: 4,
               denialOfService: 3,
               elevationOfPrivilege: 2
            }
           },
           {
             id: "c-arm",
             name: "C-Arm System",
             manufacturer: "Various (Siemens Healthineers, GE Healthcare, Philips, Ziehm Imaging)",
             description: "Mobile X-ray fluoroscopy systems",
             applications: ["Surgery", "Orthopedics", "Pain management"],
             networkConnected: true,
             ephiRiskLevel: "medium",
             threatProfiles: { // Estimated based on X-ray/Fluoroscopy
               spoofing: 2,
               tampering: 3,
               repudiation: 3,
               informationDisclosure: 4,
               denialOfService: 3,
               elevationOfPrivilege: 2
             }
           },
           {
            id: "contrast-media-injector",
            name: "Contrast Media Injector",
            manufacturer: "Various (Bayer Medrad, Bracco Diagnostics, Guerbet)", // Added examples
            description: "Automated systems for administering contrast agents during imaging",
            applications: ["MRI scans", "CT scans", "Angiography"],
            networkConnected: true, // Some models are connected for protocol sync/monitoring
            ephiRiskLevel: "low", // Data risk is low, focus on operational safety
            threatProfiles: { // Low ePHI risk, but potential safety/operational impact
              spoofing: 2,
              tampering: 3, // Incorrect dosage/protocol
              repudiation: 1,
              informationDisclosure: 1,
              denialOfService: 3,
              elevationOfPrivilege: 2
            }
          },
          {
             id: "pacs",
             name: "PACS (Picture Archiving and Communication System)",
             manufacturer: "Various (Sectra, Philips, GE, Siemens, Fujifilm)", // Added examples
             description: "Digital storage, retrieval, management, distribution, and presentation of medical images",
             applications: ["Radiology image management", "Cardiology image management", "Enterprise imaging"],
             networkConnected: true,
             ephiRiskLevel: "high",
             threatProfiles: { // High risk core imaging data system
               spoofing: 4,
               tampering: 5, // Image/report integrity
               repudiation: 4,
               informationDisclosure: 5,
               denialOfService: 5,
               elevationOfPrivilege: 4
            }
           }
          // Add more devices as needed
        ]
      },
      {
        id: "patient-monitoring",
        name: "Patient Monitoring Devices",
        description: "Devices that continuously or intermittently monitor patient vital signs",
        devices: [
          {
            id: "bedside-monitor",
            name: "Bedside Patient Monitor",
            manufacturer: "Various (Philips, GE, Mindray, Nihon Kohden)",
            description: "Multi-parameter monitoring system for continuous vital signs monitoring",
            applications: ["ICU monitoring", "General ward monitoring", "Emergency department"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: {
              spoofing: 3,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 5,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "telemetry",
            name: "Telemetry Monitoring System",
            manufacturer: "Various (Philips, GE, Spacelabs)",
            description: "Wireless vital signs monitoring for ambulatory patients",
            applications: ["Cardiac monitoring", "Post-surgical monitoring"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: {
              spoofing: 4,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "ecg-ekg",
            name: "ECG/EKG Machine",
            manufacturer: "Various (GE Healthcare, Philips, Schiller, Mortara)",
            description: "Record electrical activity of the heart",
            applications: ["Cardiac diagnostics", "Arrhythmia detection"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Estimated based on Bedside Monitor/Telemetry
              spoofing: 3,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 5,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "pulse-oximeter",
            name: "Pulse Oximeter",
            manufacturer: "Various (Masimo, Nonin, Medtronic, Philips)",
            description: "Measure oxygen saturation in blood",
            applications: ["Patient monitoring", "Respiratory assessment"],
            networkConnected: true, // Assuming network connected variant based on 'Often' in MD
            ephiRiskLevel: "medium",
            threatProfiles: { // Estimated based on medium risk monitoring devices
              spoofing: 2,
              tampering: 3,
              repudiation: 2,
              informationDisclosure: 4,
              denialOfService: 3,
              elevationOfPrivilege: 2
            }
          },
          {
            id: "bp-monitor",
            name: "Blood Pressure Monitor (Clinical)",
            manufacturer: "Various (Omron, Welch Allyn, A&D Medical, GE Healthcare)",
            description: "Measure arterial pressure in clinical setting",
            applications: ["Vital signs monitoring", "Hypertension diagnosis"],
            networkConnected: true, // Assuming network connected variant based on 'Often' in MD
            ephiRiskLevel: "medium",
            threatProfiles: { // Estimated based on medium risk monitoring devices
              spoofing: 2,
              tampering: 3,
              repudiation: 2,
              informationDisclosure: 4,
              denialOfService: 3,
              elevationOfPrivilege: 2
            }
          },
          {
            id: "fetal-monitor",
            name: "Fetal Monitor",
            manufacturer: "Various (GE Healthcare, Philips, Edan)", // Added example manu.
            description: "Monitor fetal heart rate and contractions during pregnancy",
            applications: ["Obstetrics", "Labor and delivery"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Estimated based on high-risk monitoring
              spoofing: 3,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 5,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "cerebral-function-monitor",
            name: "Cerebral Function Monitor",
            manufacturer: "Various (Natus Medical, Nihon Kohden)", // Added example manu.
            description: "Monitor brain activity",
            applications: ["Neurology", "Neonatal ICU", "Seizure monitoring"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Estimated based on high-risk monitoring
              spoofing: 3,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 5,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "cardiac-output-monitor",
            name: "Cardiac Output Monitor",
            manufacturer: "Various (Edwards Lifesciences, LiDCO)", // Added example manu.
            description: "Measure blood flow from the heart",
            applications: ["Critical care", "Anesthesia", "Hemodynamic monitoring"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Estimated based on high-risk monitoring
              spoofing: 3,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 5,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "continuous-glucose-monitor-clinical", // Differentiate from remote
            name: "Continuous Glucose Monitoring (Clinical)",
            manufacturer: "Various (Dexcom, Abbott, Medtronic - Hospital Systems)", // Specified context
            description: "Tracks blood glucose levels continuously in a clinical setting",
            applications: ["Diabetes management", "ICU monitoring"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Estimated based on high-risk monitoring
              spoofing: 3,
              tampering: 5, // Higher tampering due to direct treatment impact
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "sleep-study-equipment",
            name: "Sleep Study Equipment (Polysomnography)",
            manufacturer: "Various (Philips Respironics, Natus Medical, Cadwell)", // Added example manu.
            description: "Monitors physiological parameters during sleep",
            applications: ["Sleep disorder diagnosis", "Sleep apnea testing"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Estimated based on high-risk monitoring (large data volume)
              spoofing: 3,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "intracranial-pressure-monitor",
            name: "Intracranial Pressure Monitor",
            manufacturer: "Various (Integra LifeSciences, Medtronic)", // Added example manu.
            description: "Measures pressure inside the skull",
            applications: ["Neurosurgery", "Traumatic brain injury management"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Estimated based on high-risk monitoring
              spoofing: 3,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 5,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "bladder-scanner",
            name: "Bladder Scanner",
            manufacturer: "Various (Verathon, Laborie, Vitacon)", // Added example manu.
            description: "Ultrasound devices to measure bladder volume",
            applications: ["Urology", "Post-operative care", "Catheterization assessment"],
            networkConnected: true, // Assuming network connected variant based on 'Some'
            ephiRiskLevel: "medium",
            threatProfiles: { // Estimated based on medium-risk diagnostic
              spoofing: 2,
              tampering: 3,
              repudiation: 2,
              informationDisclosure: 4,
              denialOfService: 3,
              elevationOfPrivilege: 2
            }
          },
          // Add more devices as needed
        ]
      },
      {
        id: "telehealth",
        name: "Telehealth & Remote Patient Monitoring",
        description: "Devices for remote healthcare delivery and patient monitoring",
        devices: [
          {
            id: "telehealth-cart",
            name: "Telehealth Cart",
            manufacturer: "Various (Teladoc Health, AMD Global Telemedicine)",
            description: "Mobile cart with video conferencing equipment for virtual care",
            applications: ["Virtual consultations", "Remote specialist access"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: {
              spoofing: 4,
              tampering: 3,
              repudiation: 4,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "remote-bp",
            name: "Remote Blood Pressure Monitor",
            manufacturer: "Various (Omron, A&D Medical, Withings)",
            description: "Bluetooth or cellular-enabled blood pressure monitoring device",
            applications: ["Hypertension management", "Cardiac monitoring"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: {
              spoofing: 3,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 3,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "remote-glucose",
            name: "Remote Blood Glucose Monitor",
            manufacturer: "Various (Dexcom, Abbott, Medtronic)",
            description: "Connected glucose meters and continuous monitors",
            applications: ["Diabetes management"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: {
              spoofing: 3,
              tampering: 5,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "digital-stethoscope",
            name: "Digital Stethoscope",
            manufacturer: "Various (Eko, 3M Littmann, ThinkLabs)",
            description: "Capture and transmit heart and lung sounds remotely",
            applications: ["Telemedicine consultations", "Remote diagnostics"],
            networkConnected: true,
            ephiRiskLevel: "medium",
            threatProfiles: { // Estimated based on other medium-risk telehealth peripherals
              spoofing: 3,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 3,
              elevationOfPrivilege: 2
            }
          },
          {
            id: "remote-ecg-monitor",
            name: "Remote ECG/Heart Monitor",
            manufacturer: "Various (AliveCor, iRhythm, Zio)",
            description: "Portable cardiac monitoring devices",
            applications: ["Arrhythmia detection", "Post-discharge monitoring"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Estimated based on other remote monitors
              spoofing: 3,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 3,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "virtual-care-platform",
            name: "Virtual Care Platform Endpoint", // Representing client/device interaction
            manufacturer: "Various (Amwell, Teladoc, Zoom for Healthcare, Doxy.me)",
            description: "Software/Hardware endpoint for remote consultations",
            applications: ["Telemedicine", "Virtual visits"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // High due to direct ePHI transmission
              spoofing: 4,
              tampering: 3,
              repudiation: 4,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "digital-otoscope",
            name: "Digital Otoscope",
            manufacturer: "Various (Firefly, MedRx, Welch Allyn)",
            description: "Capture and transmit ear canal images",
            applications: ["Telemedicine ENT", "Remote ear exams"],
            networkConnected: true,
            ephiRiskLevel: "medium",
            threatProfiles: { // Estimated based on Digital Stethoscope
              spoofing: 3,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 3,
              elevationOfPrivilege: 2
            }
          },
          {
            id: "digital-dermatoscope",
            name: "Digital Dermatoscope",
            manufacturer: "Various (FotoFinder, DermLite, Canfield)",
            description: "Specialized cameras for skin examination",
            applications: ["Tele-dermatology", "Remote skin checks"],
            networkConnected: true,
            ephiRiskLevel: "medium",
            threatProfiles: { // Estimated based on Digital Stethoscope
              spoofing: 3,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 4, // Potentially sensitive images
              denialOfService: 3,
              elevationOfPrivilege: 2
            }
          },
          {
            id: "remote-pulse-oximeter",
            name: "Remote Pulse Oximeter",
            manufacturer: "Various (Masimo, Nonin, Wellue)",
            description: "Connected oxygen saturation monitors",
            applications: ["Remote patient monitoring", "Chronic respiratory disease management"],
            networkConnected: true,
            ephiRiskLevel: "medium",
            threatProfiles: { // Estimated based on Remote BP
              spoofing: 3,
              tampering: 3, 
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 3,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "remote-weight-scale",
            name: "Remote Weight Scale",
            manufacturer: "Various (Withings, A&D Medical, Tanita)",
            description: "Connected scales for weight monitoring",
            applications: ["Weight management programs", "Heart failure monitoring"],
            networkConnected: true,
            ephiRiskLevel: "medium",
            threatProfiles: { // Estimated based on Remote BP
              spoofing: 3,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 3,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "remote-spirometer",
            name: "Remote Spirometer",
            manufacturer: "Various (NuvoAir, Propeller Health, Vitalograph)",
            description: "Connected lung function monitoring devices",
            applications: ["Asthma management", "COPD monitoring"],
            networkConnected: true,
            ephiRiskLevel: "medium",
            threatProfiles: { // Estimated based on Remote BP
              spoofing: 3,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 3,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "remote-thermometer",
            name: "Remote Thermometer",
            manufacturer: "Various (Withings, Kinsa)",
            description: "Connected temperature monitors",
            applications: ["Fever tracking", "Infection monitoring"],
            networkConnected: true,
            ephiRiskLevel: "medium",
            threatProfiles: { // Estimated based on Remote BP
              spoofing: 3,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 3,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "medication-adherence-device",
            name: "Medication Adherence Device",
            manufacturer: "Various (AdhereTech, Pillsy, MedMinder)",
            description: "Smart pill dispensers and monitoring systems",
            applications: ["Medication adherence tracking", "Chronic disease management"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // High due to potential medication schedule tampering
              spoofing: 3,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "multi-parameter-hub",
            name: "Multi-Parameter Hub (RPM)",
            manufacturer: "Various (Tenovi, AccuHealth, Datos Health)",
            description: "Devices that collect data from multiple remote sources",
            applications: ["Comprehensive remote patient monitoring"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // High due to aggregation of sensitive data
              spoofing: 4,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          // Add more devices as needed
        ]
      },
      // Add more categories as needed
      {
        id: "lab-diagnostic",
        name: "Laboratory & Diagnostic Equipment",
        description: "Equipment used for laboratory analysis and point-of-care testing",
        devices: [
          {
            id: "clinical-chemistry-analyzer",
            name: "Clinical Chemistry Analyzer",
            manufacturer: "Various (Roche, Siemens Healthineers, Abbott, Beckman Coulter)",
            description: "Analyze blood chemistry",
            applications: ["Blood testing", "Metabolic panels", "Enzyme analysis"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Estimated based on High Risk Imaging/Data Systems
              spoofing: 3,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "hematology-analyzer",
            name: "Hematology Analyzer",
            manufacturer: "Various (Sysmex, Beckman Coulter, Abbott)",
            description: "Blood cell analysis",
            applications: ["Complete blood count (CBC)", "Disease diagnosis"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Estimated based on High Risk Imaging/Data Systems
              spoofing: 3,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "immunoassay-analyzer",
            name: "Immunoassay Analyzer",
            manufacturer: "Various (Roche, Abbott, Siemens Healthineers)",
            description: "Detect antibodies and antigens",
            applications: ["Infectious disease testing", "Hormone analysis", "Cancer markers"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Consistent with other lab analyzers
              spoofing: 3,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "blood-gas-analyzer",
            name: "Blood Gas Analyzer (Lab)", // Differentiate from portable
            manufacturer: "Various (Radiometer, Abbott, Siemens Healthineers)",
            description: "Measure blood gases and electrolytes",
            applications: ["Critical care testing", "Respiratory status assessment"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Consistent with other lab analyzers
              spoofing: 3,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "coagulation-analyzer",
            name: "Coagulation Analyzer (Lab)", // Differentiate from portable
            manufacturer: "Various (Instrumentation Laboratory, Siemens Healthineers, Stago)",
            description: "Measure blood clotting ability",
            applications: ["Hemostasis testing", "Anticoagulant therapy monitoring"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Consistent with other lab analyzers
              spoofing: 3,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "microbiology-system",
            name: "Microbiology System",
            manufacturer: "Various (bioMérieux, BD, Beckman Coulter)",
            description: "Identify microorganisms",
            applications: ["Infection diagnosis", "Antibiotic susceptibility testing"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Consistent with other lab analyzers
              spoofing: 3,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "urinalysis-system",
            name: "Urinalysis System",
            manufacturer: "Various (Sysmex, Beckman Coulter, Siemens Healthineers)",
            description: "Analyze urine composition",
            applications: ["Kidney disease screening", "Urinary tract infection testing"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Consistent with other lab analyzers
              spoofing: 3,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "poc-blood-analyzer",
            name: "Point-of-Care Blood Analyzer",
            manufacturer: "Various (Abbott i-STAT, Roche Cobas)", // Added example manu.
            description: "Rapid blood testing at bedside",
            applications: ["Emergency department", "ICU", "Operating room"],
            networkConnected: true, // Assuming network connected variant based on 'Often'
            ephiRiskLevel: "high",
            threatProfiles: { // High risk due to immediate clinical decision impact
              spoofing: 3,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "portable-blood-gas-analyzer",
            name: "Portable Blood Gas Analyzer",
            manufacturer: "Various (Abbott, Siemens Healthineers)", // Added example manu.
            description: "Mobile blood gas testing",
            applications: ["Point-of-care testing", "Transport medicine"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Consistent with POC blood analyzer
              spoofing: 3,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "handheld-glucose-meter",
            name: "Handheld Blood Glucose Meter (Connected)", // Specify connected variant
            manufacturer: "Various (Roche Accu-Chek, Abbott FreeStyle - Connected versions)", // Added example manu.
            description: "Measure blood glucose levels with network connectivity",
            applications: ["Point-of-care glucose testing", "Diabetes management"],
            networkConnected: true, // Assuming network connected variant based on 'Some'
            ephiRiskLevel: "medium",
            threatProfiles: { // Medium risk, less critical than continuous/lab
              spoofing: 2,
              tampering: 3,
              repudiation: 2,
              informationDisclosure: 4,
              denialOfService: 3,
              elevationOfPrivilege: 2
            }
          },
          {
            id: "rapid-diagnostic-test-reader", // More specific than just 'test'
            name: "Rapid Diagnostic Test Reader (Connected)",
            manufacturer: "Various (Quidel, BD Veritor - Connected readers)", // Added example manu.
            description: "Reader for quick diagnostic testing results transmission",
            applications: ["Infectious disease testing (Flu, Strep, COVID)", "Point-of-care diagnostics"],
            networkConnected: true, // Assuming network connected variant based on 'Some'
            ephiRiskLevel: "medium",
            threatProfiles: { // Medium risk for diagnostic results
              spoofing: 2,
              tampering: 3,
              repudiation: 2,
              informationDisclosure: 4,
              denialOfService: 3,
              elevationOfPrivilege: 2
            }
          },
          {
            id: "portable-coagulation-monitor",
            name: "Portable Coagulation Monitor (Connected)",
            manufacturer: "Various (Roche CoaguChek, Abbott INRatio - Connected versions)", // Added example manu.
            description: "Mobile blood clotting measurement with connectivity",
            applications: ["Anticoagulant monitoring (INR)", "Home testing"],
            networkConnected: true, // Assuming network connected variant based on 'Some'
            ephiRiskLevel: "medium",
            threatProfiles: { // Medium risk, similar to glucose meter
              spoofing: 2,
              tampering: 3,
              repudiation: 2,
              informationDisclosure: 4,
              denialOfService: 3,
              elevationOfPrivilege: 2
            }
          },
          {
            id: "hemoglobin-analyzer-poc",
            name: "Hemoglobin Analyzer (Point-of-Care, Connected)",
            manufacturer: "Various (HemoCue, EKF Diagnostics - Connected versions)", // Added example manu.
            description: "Measure hemoglobin at point of care with connectivity",
            applications: ["Anemia screening", "Blood donation centers"],
            networkConnected: true, // Assuming network connected variant based on 'Some'
            ephiRiskLevel: "medium",
            threatProfiles: { // Medium risk, diagnostic result
              spoofing: 2,
              tampering: 3,
              repudiation: 2,
              informationDisclosure: 4,
              denialOfService: 3,
              elevationOfPrivilege: 2
            }
          },
          // Add more devices as needed
        ]
      },
      {
        id: "ehr-systems",
        name: "Electronic Health Record Systems & Hardware",
        description: "Hardware and software systems involved in accessing and managing Electronic Health Records",
        devices: [
          {
            id: "clinical-workstation",
            name: "Clinical Workstation",
            manufacturer: "Various (Dell, HP, Lenovo, Apple)", // Added examples
            description: "Computers for accessing EHR systems",
            applications: ["EHR access", "Clinical documentation", "Order entry"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // High risk endpoint accessing core data
              spoofing: 4,
              tampering: 4,
              repudiation: 4,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 4
            }
          },
          {
            id: "mobile-clinical-cart",
            name: "Mobile Clinical Cart (WoW)",
            manufacturer: "Various (Capsa Healthcare, Ergotron, Humanscale)", // Added examples
            description: "Rolling workstations with computers (Workstation on Wheels)",
            applications: ["Bedside documentation", "Medication administration", "Mobile EHR access"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Similar to workstation, plus physical risks
              spoofing: 4,
              tampering: 4,
              repudiation: 4,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 4
            }
          },
          {
            id: "clinical-tablet-mobile",
            name: "Clinical Tablet/Mobile Device",
            manufacturer: "Various (Apple iPad, Samsung Galaxy Tab, Zebra)", // Added examples
            description: "Portable devices for EHR access",
            applications: ["Mobile charting", "Secure messaging", "Point-of-care access"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // High risk mobile endpoint
              spoofing: 4,
              tampering: 4,
              repudiation: 4,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 4
            }
          },
          {
            id: "wall-mounted-terminal",
            name: "Wall-Mounted Terminal",
            manufacturer: "Various (Advantech, Onyx Healthcare)", // Added examples
            description: "Fixed EHR access points in clinical areas",
            applications: ["In-room charting", "Patient access terminals"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Similar to workstation, physical location factor
              spoofing: 4,
              tampering: 4,
              repudiation: 4,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 4
            }
          },
          {
            id: "barcode-scanner-ehr",
            name: "Barcode Scanner (EHR Integrated)",
            manufacturer: "Various (Zebra, Honeywell, Code Corp)", // Added examples
            description: "Used for patient identification and medication administration within EHR workflow",
            applications: ["Patient ID verification", "Medication scanning (eMAR)", "Specimen tracking"],
            networkConnected: true, // Often connected via workstation/mobile device
            ephiRiskLevel: "high", // Critical part of secure workflows
            threatProfiles: { // Lower direct risk but impacts critical workflows
              spoofing: 3,
              tampering: 4, // Can impact data entry
              repudiation: 2,
              informationDisclosure: 3,
              denialOfService: 3,
              elevationOfPrivilege: 2
            }
          },
          {
            id: "rfid-system-ehr",
            name: "RFID System (Patient/Asset Tracking)",
            manufacturer: "Various (Zebra, Impinj, Stanley Healthcare)", // Added examples
            description: "Track patients, staff, and equipment, often integrated with EHR",
            applications: ["Patient tracking", "Asset management", "Staff location"],
            networkConnected: true,
            ephiRiskLevel: "high", // Location data can be sensitive
            threatProfiles: { // Focus on info disclosure and potential disruption
              spoofing: 3,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "biometric-auth-device",
            name: "Biometric Authentication Device",
            manufacturer: "Various (Imprivata, HID Global)", // Added examples
            description: "Fingerprint or retina scanners for secure EHR access",
            applications: ["User authentication", "Single sign-on"],
            networkConnected: true,
            ephiRiskLevel: "medium", // Data handled is auth, not direct PHI flow
            threatProfiles: { // Focus on auth security
              spoofing: 4,
              tampering: 4,
              repudiation: 2,
              informationDisclosure: 3,
              denialOfService: 3,
              elevationOfPrivilege: 4 // Potential EoP if compromised
            }
          },
          // Representing Software as a 'Device' endpoint for consistency
          {
            id: "hospital-information-system",
            name: "Hospital Information System (HIS) Endpoint",
            manufacturer: "Various (Epic, Cerner, MEDITECH, Allscripts)",
            description: "Core system for managing patient data across a hospital",
            applications: ["Patient registration", "Clinical documentation", "Billing"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Highest risk profile - core system
              spoofing: 5,
              tampering: 5,
              repudiation: 5,
              informationDisclosure: 5,
              denialOfService: 5,
              elevationOfPrivilege: 5
            }
          },
          {
            id: "ambulatory-ehr",
            name: "Ambulatory EHR Endpoint",
            manufacturer: "Various (eClinicalWorks, NextGen, athenahealth)",
            description: "EHR system designed for outpatient settings",
            applications: ["Clinic scheduling", "Outpatient charting", "e-Prescribing"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Similar to HIS
              spoofing: 5,
              tampering: 5,
              repudiation: 5,
              informationDisclosure: 5,
              denialOfService: 5,
              elevationOfPrivilege: 5
            }
          },
           {
            id: "pharmacy-information-system",
            name: "Pharmacy Information System (PIS) Endpoint",
            manufacturer: "Various (Cerner PharmNet, Epic Willow, McKesson)",
            description: "System for managing medication orders and dispensing",
            applications: ["Medication management", "Inventory control", "Dispensing records"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Critical for medication safety
              spoofing: 4,
              tampering: 5,
              repudiation: 4,
              informationDisclosure: 5,
              denialOfService: 5,
              elevationOfPrivilege: 4
            }
          },
          {
            id: "laboratory-information-system",
            name: "Laboratory Information System (LIS) Endpoint",
            manufacturer: "Various (Sunquest, SoftLab, Epic Beaker)",
            description: "System for managing laboratory test orders and results",
            applications: ["Lab order entry", "Result reporting", "Specimen tracking"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Critical for diagnostic accuracy
              spoofing: 4,
              tampering: 5,
              repudiation: 4,
              informationDisclosure: 5,
              denialOfService: 5,
              elevationOfPrivilege: 4
            }
          },
          {
            id: "radiology-information-system",
            name: "Radiology Information System (RIS) Endpoint",
            manufacturer: "Various (GE Centricity, Fujifilm Synapse, Sectra)", // Added example
            description: "System for managing radiology scheduling, results, and images (often integrates with PACS)",
            applications: ["Radiology scheduling", "Image management", "Reporting"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: { // Critical for imaging data
              spoofing: 4,
              tampering: 5,
              repudiation: 4,
              informationDisclosure: 5,
              denialOfService: 5,
              elevationOfPrivilege: 4
            }
          }
          // Add more devices/systems as needed
        ]
      },
      {
        id: "implantable-devices",
        name: "Implantable Medical Devices",
        description: "Medical devices implanted within the body, often with remote monitoring or programming capabilities",
        devices: [
          {
            id: "pacemaker",
            name: "Pacemaker",
            manufacturer: "Various (Medtronic, Boston Scientific, Abbott, Biotronik)", // Added Biotronik
            description: "Regulate heart rhythm",
            applications: ["Bradycardia treatment", "Heart block management"],
            networkConnected: true, // Via remote monitoring / programmer
            ephiRiskLevel: "high",
            threatProfiles: { // High impact if tampered, remote connection risk
              spoofing: 4, // Programmer/monitor spoofing
              tampering: 5, // Critical health impact
              repudiation: 3,
              informationDisclosure: 4, // Device status/patient data
              denialOfService: 4, // Prevent programming/monitoring
              elevationOfPrivilege: 4 // Unauthorized programming
            }
          },
          {
            id: "implantable-cardioverter-defibrillator",
            name: "Implantable Cardioverter-Defibrillator (ICD)",
            manufacturer: "Various (Medtronic, Boston Scientific, Abbott, Biotronik)", // Added Biotronik
            description: "Detect and correct life-threatening arrhythmias",
            applications: ["Ventricular tachycardia/fibrillation treatment", "Sudden cardiac death prevention"],
            networkConnected: true, // Via remote monitoring / programmer
            ephiRiskLevel: "high",
            threatProfiles: { // Similar to Pacemaker, potentially higher impact
              spoofing: 4,
              tampering: 5,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 5, // Critical function denial
              elevationOfPrivilege: 4
            }
          },
          {
            id: "cardiac-resynchronization-therapy",
            name: "Cardiac Resynchronization Therapy (CRT) Device",
            manufacturer: "Various (Medtronic, Boston Scientific, Abbott, Biotronik)", // Added Biotronik
            description: "Coordinate heart contractions (often combined with pacemaker/ICD)",
            applications: ["Heart failure treatment"],
            networkConnected: true, // Via remote monitoring / programmer
            ephiRiskLevel: "high",
            threatProfiles: { // Similar to ICD
              spoofing: 4,
              tampering: 5,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 5,
              elevationOfPrivilege: 4
            }
          },
          {
            id: "implantable-loop-recorder",
            name: "Implantable Loop Recorder (ILR)",
            manufacturer: "Various (Medtronic, Abbott, BIOTRONIK)",
            description: "Long-term heart rhythm monitoring",
            applications: ["Syncope diagnosis", "Atrial fibrillation detection"],
            networkConnected: true, // Via remote monitoring
            ephiRiskLevel: "high",
            threatProfiles: { // Focus on data disclosure/integrity
              spoofing: 3,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 5, // Primary function is data collection
              denialOfService: 4, // Prevent data transmission
              elevationOfPrivilege: 3
            }
          },
          {
            id: "wireless-pressure-sensor-cardiac",
            name: "Wireless Pressure Sensor (Cardiac)",
            manufacturer: "Abbott (CardioMEMS)", // Specific example
            description: "Monitor pressure within the heart or pulmonary artery",
            applications: ["Heart failure management"],
            networkConnected: true, // Via remote monitoring
            ephiRiskLevel: "high",
            threatProfiles: { // Similar to ILR
              spoofing: 3,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "neurostimulator",
            name: "Neurostimulator",
            manufacturer: "Various (Medtronic, Boston Scientific, Abbott)",
            description: "Stimulate nerves to treat conditions like chronic pain, Parkinson's, epilepsy",
            applications: ["Pain management", "Movement disorders", "Epilepsy treatment"],
            networkConnected: true, // Via programmer
            ephiRiskLevel: "high",
            threatProfiles: { // Similar to Pacemaker (programmable implant)
              spoofing: 4,
              tampering: 5,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 4,
              elevationOfPrivilege: 4
            }
          },
          {
            id: "insulin-pump-implantable", // Distinguish if needed from external pumps
            name: "Insulin Pump (Implantable or Semi-implantable)",
            manufacturer: "Various (Medtronic - some models, emerging tech)",
            description: "Deliver insulin for diabetes management (implanted components)",
            applications: ["Diabetes management"],
            networkConnected: true, // Via programmer / controller / CGM link
            ephiRiskLevel: "high",
            threatProfiles: { // Critical medication delivery
              spoofing: 4,
              tampering: 5,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 5,
              elevationOfPrivilege: 4
            }
          },
          {
            id: "cochlear-implant",
            name: "Cochlear Implant Processor", // External component is typically programmed
            manufacturer: "Various (Cochlear Ltd, MED-EL, Advanced Bionics)",
            description: "Provide sound for hearing impairment via electrical stimulation",
            applications: ["Severe-to-profound hearing loss treatment"],
            networkConnected: true, // Via programmer / remote adjustments
            ephiRiskLevel: "medium", // Less direct life-safety risk than cardiac/insulin
            threatProfiles: { // Focus on programming integrity/DoS
              spoofing: 3,
              tampering: 4,
              repudiation: 2,
              informationDisclosure: 3,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
           {
            id: "smart-joint-implant",
            name: "Smart Joint Implant Sensor", // Sensor component
            manufacturer: "Various (Zimmer Biomet Persona IQ, Canary Medical)", // Added examples
            description: "Orthopedic implants with sensors to monitor recovery/parameters",
            applications: ["Post-surgical monitoring (knee replacement)"],
            networkConnected: true, // Via base station / remote monitoring
            ephiRiskLevel: "medium", // Primarily data collection
            threatProfiles: { // Focus on data integrity/disclosure
              spoofing: 3,
              tampering: 3,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 3,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "drug-infusion-pump-implantable",
            name: "Drug Infusion Pump (Implantable)",
            manufacturer: "Various (Medtronic SynchroMed)", // Added example
            description: "Deliver medication directly to a target area (e.g., spinal cord)",
            applications: ["Chronic pain management", "Spasticity treatment"],
            networkConnected: true, // Via programmer
            ephiRiskLevel: "high",
            threatProfiles: { // Critical medication delivery
              spoofing: 4,
              tampering: 5,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 5,
              elevationOfPrivilege: 4
            }
          }
          // Add more devices as needed
        ]
      },
      {
        id: "ai-clinical-decision-support",
        name: "AI-Powered Clinical Decision Support",
        description: "AI/ML-enabled software and integrated devices for clinical decision support, diagnostics, and documentation (FDA has cleared 1,200+ AI/ML medical devices as of 2025)",
        devices: [
          {
            id: "ai-radiology-analysis",
            name: "AI Radiology Analysis System",
            manufacturer: "Various (Aidoc, Viz.ai, RapidAI, Siemens Healthineers AI-Rad, GE HealthCare AIR)",
            description: "AI/ML-powered triage and analysis software integrated with PACS for automated detection of findings in CT, MRI, and X-ray imaging",
            applications: ["Stroke detection", "Pulmonary embolism detection", "Intracranial hemorrhage", "Chest X-ray triage", "Oncology screening"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: {
              spoofing: 4,
              tampering: 5, // Adversarial input manipulation can alter AI outputs affecting clinical decisions
              repudiation: 4,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "ai-pathology-analyzer",
            name: "AI Pathology Analysis System",
            manufacturer: "Various (PathAI, Paige.AI, Proscia, Hologic)",
            description: "Digital pathology platforms with AI-assisted analysis for slide interpretation and cancer detection",
            applications: ["Cancer detection", "Tissue classification", "Biomarker analysis", "Dermatopathology"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: {
              spoofing: 4,
              tampering: 5,
              repudiation: 4,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "ai-clinical-decision-platform",
            name: "AI Clinical Decision Support Platform",
            manufacturer: "Various (Epic Cognitive Computing, Oracle Health AI, Microsoft Nuance DAX)",
            description: "AI-driven platforms integrated with EHR for treatment recommendations, risk stratification, early warning alerts, and clinical workflow optimization",
            applications: ["Sepsis prediction", "Deterioration alerts", "Medication management", "Care gap identification"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: {
              spoofing: 4,
              tampering: 5,
              repudiation: 4,
              informationDisclosure: 5,
              denialOfService: 5,
              elevationOfPrivilege: 4
            }
          },
          {
            id: "ambient-clinical-ai",
            name: "Ambient Clinical AI / AI Documentation",
            manufacturer: "Various (Microsoft Nuance DAX Copilot, Suki, Abridge, DeepScribe)",
            description: "AI systems that listen to and transcribe patient-provider conversations to auto-generate clinical notes, integrated with EHR",
            applications: ["Automated clinical documentation", "SOAP note generation", "Coding assistance"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: {
              spoofing: 4,
              tampering: 4,
              repudiation: 5, // Audio capture + auto-documentation raises significant repudiation risk
              informationDisclosure: 5,
              denialOfService: 3,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "ai-wearable-biosensor",
            name: "AI-Enabled Wearable Biosensor (Clinical)",
            manufacturer: "Various (Biolinq Shine - FDA De Novo 2025, Masimo W1, Sibel Health)",
            description: "Clinical-grade wearable biosensors with AI/ML processing for continuous multi-parameter monitoring including glucose, activity, and physiological signals",
            applications: ["Continuous glucose monitoring", "Activity and sleep tracking", "Remote patient monitoring", "Post-discharge monitoring"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: {
              spoofing: 3,
              tampering: 4,
              repudiation: 3,
              informationDisclosure: 5,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          },
          {
            id: "ai-cgm-advanced",
            name: "Advanced CGM System (15-Day Extended Wear)",
            manufacturer: "Various (Dexcom G7 15-Day - FDA cleared April 2025, Abbott Libre 3 Plus)",
            description: "Extended-wear continuous glucose monitoring systems with improved accuracy and AI-powered trend prediction, worn for up to 15 days",
            applications: ["Type 1 and Type 2 diabetes management", "Insulin dosing guidance", "Hypoglycemia prevention"],
            networkConnected: true,
            ephiRiskLevel: "high",
            threatProfiles: {
              spoofing: 3,
              tampering: 5,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 4,
              elevationOfPrivilege: 3
            }
          }
        ]
      },
      {
        id: "general-hospital-equipment",
        name: "General Hospital Equipment",
        description: "Various treatment and support equipment found in hospitals, some with increasing connectivity",
        devices: [
          {
            id: "ventilator",
            name: "Ventilator",
            manufacturer: "Various (Philips, GE Healthcare, Medtronic, Dräger, Hamilton Medical)", // Added Hamilton
            description: "Respiratory support devices providing mechanical ventilation",
            applications: ["Life support", "ICU", "Anesthesia", "Transport"],
            networkConnected: true, // Increasingly connected
            ephiRiskLevel: "high", // Critical life support
            threatProfiles: { // Critical function, focus on DoS/Tampering
              spoofing: 3,
              tampering: 5, // Altering settings is critical
              repudiation: 3,
              informationDisclosure: 4, // Ventilator settings/patient data
              denialOfService: 5, // Critical function
              elevationOfPrivilege: 4
            }
          },
          {
            id: "anesthesia-machine",
            name: "Anesthesia Machine",
            manufacturer: "Various (GE Healthcare, Dräger, Mindray, Philips)",
            description: "Deliver anesthesia gases and monitor patient during surgery",
            applications: ["Surgery", "Operating room"],
            networkConnected: true,
            ephiRiskLevel: "high", // Critical function during surgery
            threatProfiles: { // Similar to ventilator
              spoofing: 3,
              tampering: 5,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 5,
              elevationOfPrivilege: 4
            }
          },
          {
            id: "dialysis-machine",
            name: "Dialysis Machine (Hemodialysis)",
            manufacturer: "Various (Fresenius Medical Care, Baxter, B. Braun, Nikkiso)",
            description: "Blood filtration for patients with kidney failure",
            applications: ["Kidney failure treatment", "Dialysis centers", "Hospital dialysis units"],
            networkConnected: true, // Increasingly connected for monitoring/data
            ephiRiskLevel: "high", // Critical treatment process
            threatProfiles: { // Focus on treatment integrity/DoS
              spoofing: 3,
              tampering: 5, // Altering treatment parameters
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 5,
              elevationOfPrivilege: 4
            }
          },
          {
            id: "infusion-pump",
            name: "Infusion Pump (General Purpose)",
            manufacturer: "Various (B. Braun, Baxter, BD (Alaris), ICU Medical)",
            description: "Deliver fluids, medications, or nutrients intravenously",
            applications: ["Medication administration", "IV therapy", "Nutrition support"],
            networkConnected: true, // Increasingly connected (smart pumps)
            ephiRiskLevel: "high", // Critical medication delivery
            threatProfiles: { // Focus on tampering/DoS for medication safety
              spoofing: 4, // Potential for drug library spoofing
              tampering: 5,
              repudiation: 3,
              informationDisclosure: 4,
              denialOfService: 5,
              elevationOfPrivilege: 4
            }
          },
          {
            id: "patient-warming-system",
            name: "Patient Warming System",
            manufacturer: "Various (3M Bair Hugger, Stryker, Smiths Medical)",
            description: "Maintain patient body temperature during procedures or recovery",
            applications: ["Surgery", "Perioperative care", "Hypothermia prevention"],
            networkConnected: false, // Typically not connected ('Some' likely refers to monitoring aspects)
            ephiRiskLevel: "low", // Low data risk, primarily safety device
            threatProfiles: { // Minimal digital threat
              spoofing: 1,
              tampering: 2, // Physical tampering risk
              repudiation: 1,
              informationDisclosure: 1,
              denialOfService: 2, // Device failure risk
              elevationOfPrivilege: 1
            }
          },
          {
            id: "defibrillator",
            name: "Defibrillator/Monitor",
            manufacturer: "Various (Philips, Zoll, Stryker (Physio-Control), Nihon Kohden)",
            description: "Deliver electrical shock to heart and monitor ECG/vitals",
            applications: ["Cardiac arrest treatment", "Emergency response", "Patient monitoring"],
            networkConnected: true, // Increasingly connected for data transmission
            ephiRiskLevel: "medium", // Primarily event-based data, but critical function
            threatProfiles: { // Balance between critical function and data risk
              spoofing: 3,
              tampering: 4, // Critical function tampering
              repudiation: 3,
              informationDisclosure: 4, // ECG data, event logs
              denialOfService: 4, // Prevent use in emergency
              elevationOfPrivilege: 3
            }
          },
           {
            id: "lithotripter",
            name: "Lithotripter",
            manufacturer: "Various (Dornier MedTech, Richard Wolf, Olympus, Storz Medical)", // Added Storz
            description: "Use shock waves to break up stones in kidneys, gallbladder, etc.",
            applications: ["Kidney stone treatment", "Gallstone treatment"],
            networkConnected: true, // Some connected for imaging/planning
            ephiRiskLevel: "medium", // Less direct ePHI flow, more operational tech
            threatProfiles: { // Focus on operational integrity
              spoofing: 2,
              tampering: 3,
              repudiation: 2,
              informationDisclosure: 3,
              denialOfService: 3,
              elevationOfPrivilege: 2
            }
          },
          {
            id: "nurse-call-system",
            name: "Nurse Call System",
            manufacturer: "Various (Rauland, Hillrom, Ascom, Jeron)", // Added examples
            description: "Patient-to-nurse communication system, often integrated",
            applications: ["Patient requests", "Staff communication", "Alerts"],
            networkConnected: true,
            ephiRiskLevel: "medium", // Can contain patient location/request info
            threatProfiles: { // Focus on availability and potential info leak
              spoofing: 3,
              tampering: 3,
              repudiation: 2,
              informationDisclosure: 3,
              denialOfService: 4, // Impact on response time
              elevationOfPrivilege: 2
            }
          },
          {
            id: "medical-refrigerator",
            name: "Medical Refrigerator/Freezer (Monitored)",
            manufacturer: "Various (Helmer Scientific, Follett Healthare, Thermo Fisher)", // Added examples
            description: "Store temperature-sensitive medications, vaccines, specimens with monitoring",
            applications: ["Pharmacy storage", "Lab sample storage", "Blood bank"],
            networkConnected: true, // Increasingly for temp monitoring/alerts
            ephiRiskLevel: "medium", // Indirect risk via spoilage impacting treatment
            threatProfiles: { // Focus on monitoring integrity/alerts
              spoofing: 2,
              tampering: 3, // Tampering with monitoring/alarms
              repudiation: 2,
              informationDisclosure: 2, // Temp logs, potentially inventory
              denialOfService: 3, // Failure of monitoring/alerts
              elevationOfPrivilege: 2
            }
          },
          {
            id: "automated-dispensing-cabinet",
            name: "Automated Dispensing Cabinet (ADC)",
            manufacturer: "Various (Omnicell, BD Pyxis)", // Added examples
            description: "Secure medication storage and dispensing, tracking access",
            applications: ["Medication management", "Controlled substance storage", "Inventory control"],
            networkConnected: true,
            ephiRiskLevel: "high", // Direct link to medication records and access
            threatProfiles: { // High risk for access control/medication diversion
              spoofing: 4,
              tampering: 5, // Inventory/dispensing manipulation
              repudiation: 4, // Access logs critical
              informationDisclosure: 4, // Dispensing records
              denialOfService: 4,
              elevationOfPrivilege: 4
            }
          },
           {
            id: "environmental-monitoring-system",
            name: "Environmental Monitoring System Sensor",
            manufacturer: "Various (Seneca, Rees Scientific)", // Added examples
            description: "Monitor temperature, humidity, pressure in critical areas",
            applications: ["Operating rooms", "Pharmacies", "Labs"],
            networkConnected: true, // Increasingly connected
            ephiRiskLevel: "low", // Data is environmental, indirect impact
            threatProfiles: { // Focus on monitoring integrity
              spoofing: 2,
              tampering: 3, // False readings/alarms
              repudiation: 1,
              informationDisclosure: 1,
              denialOfService: 3, // Loss of monitoring
              elevationOfPrivilege: 1
            }
          },
          {
            id: "medical-gas-system",
            name: "Medical Gas Outlet/System Component",
            manufacturer: "Various (BeaconMedaes, Dräger - Infrastructure)", // Added examples
            description: "Wall outlets and components supplying medical gases (Oxygen, Air, Vacuum)",
            applications: ["Patient rooms", "Operating rooms", "ICUs"],
            networkConnected: false, // Typically infrastructure, though central monitoring may exist
            ephiRiskLevel: "low", // No ePHI, purely operational/safety
            threatProfiles: { // Physical/Infrastructure Risk
              spoofing: 1,
              tampering: 2, // Physical tampering
              repudiation: 1,
              informationDisclosure: 1,
              denialOfService: 3, // Critical failure potential
              elevationOfPrivilege: 1
            }
          },
          {
            id: "pneumatic-tube-system",
            name: "Pneumatic Tube System Station",
            manufacturer: "Various (Swisslog, Pevco, Aerocom)", // Added examples
            description: "System for transporting physical items (specimens, meds) via air tubes",
            applications: ["Lab sample transport", "Pharmacy transport"],
            networkConnected: true, // Station control/tracking often networked
            ephiRiskLevel: "low", // ePHI risk relates to physical item security, not system data
            threatProfiles: { // Focus on availability and tracking system security
              spoofing: 2,
              tampering: 2, // Physical interception or system manipulation
              repudiation: 2, // Tracking logs
              informationDisclosure: 1,
              denialOfService: 3, // System availability
              elevationOfPrivilege: 2
            }
          }
          // Add more low-risk/non-connected devices as needed (e.g., Med Gas, Pneumatic Tubes, RFID Asset Tracking)
        ]
      }
    ],
    
    riskLevels: {
      "low": {
        description: "Minimal ePHI risk, limited connectivity, or minimal patient impact if compromised",
        recommendedActions: "Annual review and standard security controls"
      },
      "medium": {
        description: "Moderate ePHI risk, some connectivity, potential patient impact if compromised",
        recommendedActions: "Bi-annual review, enhanced security controls, regular updates"
      },
      "high": {
        description: "Significant ePHI risk, network connected, direct patient impact if compromised",
        recommendedActions: "Quarterly review, comprehensive security controls, regular updates and monitoring"
      }
    },
    
    securityConsiderations: [
      {
        id: "data-storage",
        name: "Data Storage & Transmission", 
        description: "Whether the device stores or transmits ePHI",
        assessmentQuestions: [
          "Does the device store patient data locally?",
          "Does the device transmit patient data to other systems?",
          "Is the data encrypted when stored?",
          "Is the data encrypted during transmission?"
        ]
      },
      {
        id: "network-connectivity",
        name: "Network Connectivity", 
        description: "How the device connects to networks",
        assessmentQuestions: [
          "Is the device connected to the internal network?",
          "Does the device connect to the internet?",
          "Does the device use wireless communication?",
          "Are network ports restricted to only necessary connections?"
        ]
      },
      {
        id: "authentication",
        name: "Authentication", 
        description: "How users authenticate to the device",
        assessmentQuestions: [
          "Does the device require user authentication?",
          "Does the device support multi-factor authentication?",
          "Has the default password been changed?",
          "Does the device enforce password complexity requirements?"
        ]
      },
      {
        id: "software-updates",
        name: "Software Updates", 
        description: "How security updates are managed",
        assessmentQuestions: [
          "Is the device still supported by the manufacturer?",
          "Is there a process for applying security updates?",
          "How quickly are security patches applied?",
          "Does the device run on a supported operating system?"
        ]
      },
      {
        id: "end-of-life",
        name: "End-of-Life", 
        description: "Device support status",
        assessmentQuestions: [
          "Is the device approaching end-of-life status?",
          "Is there a replacement plan for end-of-life devices?",
          "Are there compensating controls for devices that cannot be updated?"
        ]
      }
    ]
  };
  
  export default medicalDevicesDatabase;

  // Enhanced regulatory matrix and vulnerabilities for medical-devices-database.js

export const regulatoryMatrix = {
    hipaa: {
      name: "HIPAA/HITECH",
      description: "Health Insurance Portability and Accountability Act and Health Information Technology for Economic and Clinical Health Act",
      requirements: [
        { id: "h1", name: "Risk Analysis", citation: "§164.308(a)(1)(ii)(A)", 
          description: "Conduct an accurate and thorough assessment of the potential risks and vulnerabilities to the confidentiality, integrity, and availability of ePHI.",
          deviceRelevance: "Applies to all medical devices that store, process, or transmit ePHI." },
        { id: "h2", name: "Risk Management", citation: "§164.308(a)(1)(ii)(B)", 
          description: "Implement security measures sufficient to reduce risks and vulnerabilities to a reasonable and appropriate level.",
          deviceRelevance: "Requires implementing controls to mitigate risks identified in medical devices." },
        { id: "h3", name: "Security Incident Procedures", citation: "§164.308(a)(6)(i)", 
          description: "Implement policies and procedures to address security incidents.",
          deviceRelevance: "Must include procedures for handling device security breaches or compromises." },
        { id: "h4", name: "Access Control", citation: "§164.312(a)(1)", 
          description: "Implement technical policies and procedures for electronic information systems that maintain ePHI to allow access only to authorized persons or software programs.",
          deviceRelevance: "Requires authentication and authorization controls for device access." },
        { id: "h5", name: "Audit Controls", citation: "§164.312(b)", 
          description: "Implement hardware, software, and/or procedural mechanisms that record and examine activity in information systems containing ePHI.",
          deviceRelevance: "Requires logging and monitoring capabilities in medical devices." },
        { id: "h6", name: "Integrity", citation: "§164.312(c)(1)", 
          description: "Implement policies and procedures to protect ePHI from improper alteration or destruction.",
          deviceRelevance: "Requires controls to prevent unauthorized modification of data on medical devices." },
        { id: "h7", name: "Transmission Security", citation: "§164.312(e)(1)", 
          description: "Implement technical security measures to guard against unauthorized access to ePHI being transmitted over an electronic communications network.",
          deviceRelevance: "Applies to all networked medical devices transmitting patient data." },
        { id: "h8", name: "Device and Media Controls", citation: "§164.310(d)(1)", 
          description: "Implement policies and procedures that govern the receipt and removal of hardware and electronic media that contain ePHI.",
          deviceRelevance: "Applies to management of medical devices throughout their lifecycle." },
        { id: "h9", name: "Evaluation", citation: "§164.308(a)(8)", 
          description: "Perform a periodic technical and nontechnical evaluation based initially upon the standards implemented under this rule and subsequently in response to environmental or operational changes.",
          deviceRelevance: "Requires periodic re-evaluation of medical device security." },
        { id: "h10", name: "Security Awareness and Training", citation: "§164.308(a)(5)",
          description: "Implement a security awareness and training program for all members of the workforce.",
          deviceRelevance: "Staff must be trained on secure use and management of medical devices." },
        { id: "h11", name: "Multi-Factor Authentication (MFA) — Mandatory", citation: "§164.312(d) — 2025 NPRM / Expected Final Rule May 2026",
          description: "The expected final rule (publication anticipated May 2026, 180-day compliance window → November 2026) makes MFA mandatory for all remote access to ePHI-containing systems — EHR login, VPN, cloud platforms, and all off-campus connections. The 'addressable' classification is eliminated entirely; MFA is a baseline requirement with no risk-based opt-out.",
          deviceRelevance: "Networked medical devices and workstations accessing ePHI must implement MFA for all remote access. Devices that cannot natively support MFA require network-level compensating controls (e.g., jump servers, device-specific VPN with MFA at the gateway) with documented risk acceptance. Begin MFA roadmapping now — November 2026 is the expected compliance deadline." },
        { id: "h12", name: "Encryption — Mandatory Standards", citation: "§164.312(a)(2)(iv) & §164.312(e)(2)(ii) — 2025 NPRM / Expected Final Rule May 2026",
          description: "The expected final rule mandates encryption of all ePHI with no exceptions: AES-256 or equivalent for data at rest, TLS 1.2 or higher for data in transit. The prior 'addressable' option allowing risk-based alternatives to encryption is eliminated.",
          deviceRelevance: "All medical devices storing or transmitting ePHI must meet these standards. Legacy devices that cannot support AES-256 at rest or TLS 1.2+ in transit are non-compliant and require compensating controls or replacement planning prior to November 2026." },
        { id: "h13", name: "Vulnerability Scanning — Quarterly Minimum", citation: "§164.308(a)(8) — 2025 NPRM / Expected Final Rule May 2026",
          description: "The expected final rule requires vulnerability scanning of all networks and systems handling ePHI at least quarterly (a tightening from the NPRM's 'every six months'). Organizations with high-risk environments are encouraged to adopt monthly or continuous scanning. Formal remediation documentation is required.",
          deviceRelevance: "Medical devices must be included in quarterly vulnerability scanning programs. Use FDA-provided SBOMs to identify vulnerable components. Coordinate with manufacturers on authorized scanning approaches — unauthorized scans can affect device safety and may void warranties or FDA clearances." },
        { id: "h14", name: "ePHI Asset Inventory", citation: "§164.308(a)(1) — 2025 NPRM / Expected Final Rule May 2026",
          description: "The expected final rule requires a documented inventory of all systems, devices, and media that create, receive, maintain, or transmit ePHI — including servers, databases, backup media, cloud instances, and networked medical devices.",
          deviceRelevance: "Every networked medical device handling ePHI must appear in the asset inventory. This is the foundational requirement that enables vulnerability scanning, risk assessment, and compliance documentation." },
        { id: "h15", name: "Business Associate 24-Hour Breach Notification", citation: "§164.308(b) / §164.502(e) — 2025 NPRM / Expected Final Rule May 2026",
          description: "The expected final rule tightens Business Associate Agreement requirements: BAs must report breaches within 24 hours of discovery (down from the current 60 days). BAAs must explicitly require BAs to implement encryption, MFA, and vulnerability scanning standards equivalent to covered entity obligations.",
          deviceRelevance: "Medical device manufacturers and managed service providers with ePHI access are business associates. Update procurement contracts and BAAs to require 24-hour breach notification and mandate equivalent cybersecurity controls before November 2026." }
      ]
    },
    fda: {
      name: "FDA Medical Device Regulations",
      description: "Food and Drug Administration requirements for medical device security and safety. Significant 2026 update: FDA issued revised cybersecurity premarket guidance February 9, 2026, aligning with the Quality Management System Regulation (QMSR) effective February 2, 2026.",
      requirements: [
        { id: "f1", name: "Design Controls", citation: "ISO 13485 Clause 7.3 (formerly 21 CFR 820.30)",
          description: "Establish and maintain procedures to control the design of the device to ensure that specified design requirements are met. Note: The QMSR (effective Feb 2, 2026) replaced 21 CFR 820 with ISO 13485:2016 — all legacy 21 CFR 820.30 references now map to ISO 13485 Clause 7.3.",
          deviceRelevance: "Requires security to be built into medical device design process. Under QMSR, cybersecurity is now a formal quality system element in design controls." },
        { id: "f2", name: "Risk Management", citation: "ISO 13485 Clause 7.1 (formerly 21 CFR 820.30(g))",
          description: "Design validation shall include risk analysis. Under QMSR, security risk management is embedded in ISO 13485 Clause 7.1 risk management processes.",
          deviceRelevance: "Security risks must be assessed as part of device validation. QMSR makes cybersecurity risk management a quality system requirement, not just a guidance recommendation." },
        { id: "f3", name: "Cybersecurity Premarket Submissions (QMSR-Aligned)", citation: "FDA Guidance February 9, 2026 (revised); FDA Final Guidance June 27, 2025 (Section 524B FD&C Act)",
          description: "FDA issued revised premarket cybersecurity guidance February 9, 2026, aligning the June 2025 guidance with the QMSR. No new technical cybersecurity requirements were added — the revision replaces all 21 CFR 820 references with corresponding ISO 13485:2016 clauses. Premarket submissions must include: (1) Security Risk Management Report, (2) Software Bill of Materials (SBOM), (3) Secure Product Development Framework (SPDF) documentation, (4) detailed architecture views for exploitability assessment.",
          deviceRelevance: "When evaluating new device acquisitions, require vendors to provide SBOM and evidence of SPDF compliance. Verify new devices were cleared/approved under the February 2026 QMSR-aligned standard." },
        { id: "f4", name: "Quality Management System Regulation (QMSR)", citation: "21 CFR Part 820 / QMSR — Effective February 2, 2026",
          description: "The QMSR replaced the Quality System Regulation (QSR) on February 2, 2026. It incorporates ISO 13485:2016 as the foundation for medical device quality management. Critically for cybersecurity: security requirements are now embedded across ISO 13485 clauses — Risk Management (7.1), Design Controls (7.3.7), Data Analysis (8.4), and CAPA (8.5) — making cybersecurity a core quality system obligation rather than a separate technical consideration.",
          deviceRelevance: "Devices manufactured or modified after February 2, 2026 must comply with QMSR. Healthcare organizations should verify that device vendors have transitioned QMS documentation to QMSR/ISO 13485 compliance, particularly for cybersecurity integration into design controls and CAPA processes." },
        { id: "f5", name: "Secure Product Development Framework (SPDF)", citation: "FDA Guidance Feb 9, 2026 / Section 524B FD&C Act",
          description: "The SPDF is required for all 'cyber devices' and encompasses: threat modeling, secure architecture design, source code review, penetration testing, SBOM management, and coordinated vulnerability disclosure processes. The Feb 2026 guidance reaffirmed SPDF as the core cybersecurity development methodology under QMSR.",
          deviceRelevance: "Request SPDF documentation and evidence of threat modeling from device manufacturers. SPDF compliance indicates the manufacturer has systematically addressed cybersecurity throughout development — a key procurement evaluation criterion." },
        { id: "f6", name: "2025 Cybersecurity Final Guidance — Cyber Devices & SBOM", citation: "FDA Final Guidance June 27, 2025 (Section 524B FD&C Act) — superseded by Feb 9, 2026 revision for QMSR alignment",
          description: "FDA's June 2025 guidance formally established 'cyber devices' as a distinct legal category. Manufacturers must provide a Software Bill of Materials (SBOM) — mandatory, not optional — and notify customers within 30 days of vulnerability discovery. Vulnerability resolution required within 60 days. The February 9, 2026 revision updated this guidance to align with QMSR/ISO 13485 but did not change these core requirements.",
          deviceRelevance: "Request SBOMs from all device manufacturers for every device in your inventory. Establish a formal process for receiving and triaging 30-day vulnerability notifications. Verify resolution within 60 days or document compensating controls." },
        { id: "f7", name: "Post-Market Lifecycle Security — Coordinated Vulnerability Disclosure", citation: "FDA Guidance / Section 524B FD&C Act / QMSR Clause 8.5",
          description: "Manufacturers of cyber devices must monitor, identify, and address post-market cybersecurity vulnerabilities throughout the device lifecycle. Under QMSR, vulnerability management is embedded in CAPA (Clause 8.5). FDA enforcement focus in 2026 is auditing real-world effectiveness of post-market security programs, not just pre-market documentation.",
          deviceRelevance: "Healthcare organizations should require evidence of manufacturer CVD program compliance in procurement contracts. Maintain an inventory of received vulnerability notifications and document remediation timelines for each device." },
        { id: "f8", name: "Medical Device Reporting & Unique Device Identification", citation: "21 CFR 803 / 21 CFR 801 Subpart B",
          description: "Requirements for reporting adverse events, product problems, corrections, and removals. Security vulnerabilities may qualify as reportable product problems. Unique Device Identification (UDI) enables precise device tracking across inventory, vulnerability, and recall management.",
          deviceRelevance: "Include UDIs in your device asset inventory to enable rapid identification across vulnerability databases, FDA recalls, and SBOM component tracking." }
      ]
    },
    nist: {
      name: "NIST Cybersecurity Framework 2.0",
      description: "NIST CSF 2.0 (released February 2024) expands the original framework with a new Govern function and updated guidance for critical infrastructure including healthcare. Supersedes CSF 1.1.",
      requirements: [
        { id: "n0", name: "Govern (NEW — CSF 2.0)", citation: "NIST CSF 2.0 Function — GV",
          description: "New in CSF 2.0: Establishes and monitors the organization's cybersecurity risk management strategy, expectations, and policy. Covers roles, responsibilities, supply chain risk, and oversight. Addresses how cybersecurity decisions are made and accountability is assigned.",
          deviceRelevance: "Requires formal governance structures for medical device cybersecurity — including executive ownership, defined roles for clinical engineering/IT/security, and supply chain risk policies for device vendors and third-party components." },
        { id: "n1", name: "Identify", citation: "NIST CSF 2.0 Function — ID",
          description: "Develop organizational understanding to manage cybersecurity risk to systems, assets, data, and capabilities.",
          deviceRelevance: "Requires inventory of medical devices and understanding their security context." },
        { id: "n2", name: "Protect", citation: "NIST CSF 2.0 Function — PR",
          description: "Develop and implement appropriate safeguards to ensure delivery of critical infrastructure services.",
          deviceRelevance: "Implement controls to protect medical device functionality and data." },
        { id: "n3", name: "Detect", citation: "NIST CSF 2.0 Function — DE",
          description: "Develop and implement appropriate activities to identify the occurrence of a cybersecurity event.",
          deviceRelevance: "Implement monitoring to detect security issues with medical devices." },
        { id: "n4", name: "Respond", citation: "NIST CSF 2.0 Function — RS",
          description: "Develop and implement appropriate activities to take action regarding a detected cybersecurity event.",
          deviceRelevance: "Have procedures for responding to medical device security incidents." },
        { id: "n5", name: "Recover", citation: "NIST CSF 2.0 Function — RC",
          description: "Develop and implement appropriate activities to maintain plans for resilience and to restore capabilities or services impaired due to a cybersecurity event.",
          deviceRelevance: "Have plans for recovering medical device functionality after a security incident. HIPAA NPRM 2025 mandates 72-hour recovery for critical ePHI systems — align recovery targets with this requirement." }
      ]
    },
    hicp: {
      name: "HHS 405(d) — Health Industry Cybersecurity Practices (HICP)",
      description: "Health Industry Cybersecurity Practices (HICP) published by the HHS 405(d) Task Group. Provides consensus-based, voluntary cybersecurity practices for the healthcare sector. Recognized by HHS as the primary voluntary cybersecurity framework for HIPAA-covered entities seeking to demonstrate 'recognized security practices' under the HITECH Act (affirmative defense provision).",
      requirements: [
        { id: "hc1", name: "Email Protection Systems", citation: "HICP Practice 1",
          description: "Implementing technical controls to protect against phishing and email-based attacks — the #1 threat vector in healthcare.",
          deviceRelevance: "Medical device management and clinical engineering teams are frequent phishing targets. Compromised email accounts are used to gain initial network access before lateral movement to device networks." },
        { id: "hc2", name: "Endpoint Protection Systems", citation: "HICP Practice 2",
          description: "Deploying endpoint detection and response (EDR) on all clinical workstations and networked devices where technically feasible.",
          deviceRelevance: "Many medical devices run legacy OS (Windows XP/7) where EDR cannot be installed. For these, network-based detection and segmentation are compensating controls." },
        { id: "hc3", name: "Identity and Access Management", citation: "HICP Practice 3",
          description: "Managing user identities, access privileges, and authentication for clinical systems.",
          deviceRelevance: "Unique user IDs for device access and role-based access control align directly with HIPAA §164.312(a)(2)(i) and the 2025 NPRM's mandatory MFA requirement." },
        { id: "hc4", name: "Data Protection and Loss Prevention", citation: "HICP Practice 4",
          description: "Protecting ePHI from unauthorized access, exfiltration, or destruction.",
          deviceRelevance: "Covers encryption at rest and in transit for device-generated ePHI — a mandatory control under the 2025 HIPAA NPRM." },
        { id: "hc5", name: "Asset Management", citation: "HICP Practice 5",
          description: "Maintaining a current inventory of all hardware and software assets, including networked medical devices.",
          deviceRelevance: "Complete device inventory is a prerequisite for risk assessment, SBOM collection, and vulnerability scanning under both HICP and FDA 2025 guidance." },
        { id: "hc6", name: "Network Management", citation: "HICP Practice 6",
          description: "Segmenting clinical networks to limit lateral movement and contain security incidents.",
          deviceRelevance: "Network segmentation is the primary compensating control for legacy medical devices that cannot be patched or have EDR installed." },
        { id: "hc7", name: "Vulnerability Management", citation: "HICP Practice 7",
          description: "Identifying and remediating known vulnerabilities in clinical systems and devices.",
          deviceRelevance: "Aligns with HIPAA NPRM mandatory vulnerability scanning (every 6 months) and annual penetration testing. Use FDA-provided SBOMs to identify vulnerable components." },
        { id: "hc8", name: "Incident Response", citation: "HICP Practice 8",
          description: "Establishing documented procedures for detecting, responding to, and recovering from cybersecurity incidents.",
          deviceRelevance: "Required under HIPAA §164.308(a)(6). HICP specifically addresses medical device-specific incident scenarios (ransomware disabling infusion pumps, imaging systems offline)." },
        { id: "hc9", name: "Medical Device Security", citation: "HICP Practice 9",
          description: "Dedicated practice for managing cybersecurity risks of networked medical devices — the only major healthcare framework with a device-specific practice.",
          deviceRelevance: "Directly applicable. Covers procurement security requirements, asset inventory, patch management workflows for FDA-regulated devices, and vendor coordination for vulnerability disclosure." },
        { id: "hc10", name: "Cybersecurity Policies", citation: "HICP Practice 10",
          description: "Establishing formal cybersecurity policies, training programs, and governance structures.",
          deviceRelevance: "Provides the policy framework that makes individual device risk assessments meaningful. Includes workforce training requirements that overlap with HIPAA §164.308(a)(5)." }
      ]
    },
    iso: {
      name: "ISO/IEC 27001 and 80001",
      description: "International standards for information security management and risk management for IT-networks incorporating medical devices",
      requirements: [
        { id: "i1", name: "Information Security Management System", citation: "ISO/IEC 27001", 
          description: "Requirements for establishing, implementing, maintaining and continually improving an information security management system.",
          deviceRelevance: "Framework for managing security of medical device ecosystems." },
        { id: "i2", name: "Application of Risk Management", citation: "ISO 80001-1:2021", 
          description: "Application of risk management for IT-networks incorporating medical devices.",
          deviceRelevance: "Specific standard for managing risks of networked medical devices." },
        { id: "i3", name: "Security Assessment", citation: "ISO/IEC 27005", 
          description: "Information security risk management guidelines.",
          deviceRelevance: "Framework for assessing security risks of medical devices." },
        { id: "i4", name: "Incident Management", citation: "ISO/IEC 27035", 
          description: "Information security incident management guidelines.",
          deviceRelevance: "Process for managing security incidents affecting medical devices." }
      ]
    },
    mdic: {
      name: "Medical Device Innovation Consortium",
      description: "Joint Security Plan framework for improving medical device security",
      requirements: [
        { id: "m1", name: "Security by Design", citation: "JSP Framework", 
          description: "Integrating security into the product development process.",
          deviceRelevance: "Entire product lifecycle approach to medical device security." },
        { id: "m2", name: "Third-Party Components", citation: "JSP Framework", 
          description: "Managing security of third-party and off-the-shelf components.",
          deviceRelevance: "Addresses the security of components used in medical devices." },
        { id: "m3", name: "Threat Modeling", citation: "JSP Framework", 
          description: "Systematic approach to identifying potential threats and vulnerabilities.",
          deviceRelevance: "Methodology for identifying security risks in medical devices." }
      ]
    },
    eu: {
      name: "EU MDR, IVDR, and AI Act",
      description: "European Union Medical Device Regulation, In Vitro Diagnostic Regulation, and the EU AI Act (in force August 2024). Applies to any organization placing medical devices or AI systems on the EU market.",
      requirements: [
        { id: "e1", name: "Essential Requirements", citation: "EU MDR Article 5",
          description: "General safety and performance requirements.",
          deviceRelevance: "Security is part of safety requirements for medical devices in EU." },
        { id: "e2", name: "Risk Management", citation: "EU MDR Annex I",
          description: "Risk management system requirements.",
          deviceRelevance: "Requires addressing security risks as part of overall risk management." },
        { id: "e3", name: "Post-Market Surveillance", citation: "EU MDR Article 83",
          description: "Requirements for monitoring devices after they are placed on the market.",
          deviceRelevance: "Includes monitoring for security vulnerabilities and incidents." },
        { id: "e4", name: "EU AI Act — High-Risk AI Systems", citation: "EU AI Act (Regulation 2024/1689), Annex III",
          description: "The EU AI Act (in force August 1, 2024; key provisions applying from August 2026) classifies AI systems used in medical devices and clinical decision support as High-Risk AI. Requires conformity assessment, technical documentation, human oversight measures, accuracy/robustness/cybersecurity standards, and registration in the EU AI database before market placement.",
          deviceRelevance: "AI Clinical Decision Support systems, AI-powered diagnostic imaging, and AI-enabled implantables sold or operated in the EU require full AI Act conformity assessment. Healthcare organizations deploying these systems bear shared obligations alongside manufacturers. Applies to all 6 devices in the AI Clinical Decision Support category and AI-enabled variants in other categories." },
        { id: "e5", name: "EU AI Act — Transparency Obligations", citation: "EU AI Act Article 50",
          description: "AI systems that interact with humans or generate synthetic medical content must disclose their AI nature. Applies from August 2, 2026.",
          deviceRelevance: "Ambient clinical AI, AI documentation assistants, and AI diagnostic tools used with patients must disclose their AI nature. Relevant for Nuance DAX, AI radiology readers, and similar systems in the database." },
        { id: "e6", name: "NIS2 Directive", citation: "EU Directive 2022/2555 — effective October 18, 2024",
          description: "The EU NIS2 Directive significantly expands cybersecurity obligations for healthcare entities across EU member states. Healthcare is now classified as an 'Essential Entity' (previously 'important'). Requires risk management measures, supply chain security, incident reporting within 24 hours (initial) and 72 hours (detailed), and management accountability for cybersecurity.",
          deviceRelevance: "EU healthcare organizations must report medical device-related cyber incidents within 24 hours of awareness. Supply chain security requirements apply to medical device vendors and software suppliers. Management bodies (boards) are personally liable for NIS2 compliance failures." }
      ]
    }
  };
  
  export const knownVulnerabilities = {
    categories: [
      {
        id: "authentication",
        name: "Authentication Issues",
        description: "Vulnerabilities related to device authentication mechanisms",
        vulnerabilities: [
          { 
            id: "auth1", 
            name: "Default Credentials", 
            cve: "Various (e.g., CVE-2019-10966, CVE-2020-25183)",
            description: "Many medical devices ship with default passwords that are easily discoverable and often remain unchanged.",
            affectedDeviceTypes: ["infusion-pumps", "patient-monitors", "imaging-systems", "ventilators"],
            severity: "Critical",
            mitigation: "Change default passwords, implement strong password policies, use multi-factor authentication where possible."
          },
          { 
            id: "auth2", 
            name: "Hardcoded Credentials", 
            cve: "Various (e.g., CVE-2020-12040, CVE-2020-25179)",
            description: "Credentials embedded in firmware or software that cannot be changed, creating permanent backdoors.",
            affectedDeviceTypes: ["imaging-systems", "patient-monitors", "surgical-robots"],
            severity: "Critical",
            mitigation: "Require vendor to update firmware, implement network segmentation, use compensating controls."
          },
          { 
            id: "auth3", 
            name: "Weak Authentication", 
            cve: "Various (e.g., CVE-2020-16222, CVE-2019-3986)",
            description: "Authentication mechanisms that are easily bypassed, such as short PINs or insecure session management.",
            affectedDeviceTypes: ["infusion-pumps", "patient-monitors", "telemetry-systems"],
            severity: "High",
            mitigation: "Implement strong credential requirements, session timeout controls, and access monitoring."
          },
          { 
            id: "auth4", 
            name: "Authentication Bypass", 
            cve: "Various (e.g., CVE-2020-10886, CVE-2020-12047)",
            description: "Flaws allowing attackers to circumvent authentication entirely through technical exploits.",
            affectedDeviceTypes: ["imaging-systems", "surgical-robots", "infusion-pumps"],
            severity: "Critical",
            mitigation: "Apply vendor patches promptly, implement network segmentation, use web application firewalls."
          }
        ]
      },
      {
        id: "encryption",
        name: "Encryption and Data Protection",
        description: "Vulnerabilities related to inadequate data protection",
        vulnerabilities: [
          { 
            id: "enc1", 
            name: "Unencrypted Data Storage", 
            cve: "Various (e.g., CVE-2019-11687)",
            description: "Patient data or credentials stored without encryption on device storage.",
            affectedDeviceTypes: ["imaging-systems", "patient-monitors", "diagnostic-equipment"],
            severity: "High",
            mitigation: "Implement storage encryption, minimize sensitive data stored on devices."
          },
          { 
            id: "enc2", 
            name: "Unencrypted Communications", 
            cve: "Various (e.g., CVE-2020-14542, CVE-2019-10966)",
            description: "Data transmitted between devices or to servers without encryption, vulnerable to interception.",
            affectedDeviceTypes: ["telehealth-devices", "remote-monitoring", "wireless-medical-devices"],
            severity: "High",
            mitigation: "Implement TLS for all communications, verify certificate validation."
          },
          { 
            id: "enc3", 
            name: "Improper Certificate Validation", 
            cve: "Various (e.g., CVE-2020-12049)",
            description: "Failure to properly validate TLS/SSL certificates, enabling man-in-the-middle attacks.",
            affectedDeviceTypes: ["telehealth-devices", "connected-medical-devices", "remote-monitoring"],
            severity: "High",
            mitigation: "Implement proper certificate validation, use certificate pinning."
          },
          { 
            id: "enc4", 
            name: "Weak Encryption Algorithms", 
            cve: "Various",
            description: "Use of outdated or insecure encryption algorithms (e.g., DES, MD5, SHA-1).",
            affectedDeviceTypes: ["legacy-devices", "older-imaging-systems"],
            severity: "Medium",
            mitigation: "Update to current encryption standards (AES-256, SHA-256), phase out legacy devices."
          }
        ]
      },
      {
        id: "software",
        name: "Software and Firmware Issues",
        description: "Vulnerabilities in device software components",
        vulnerabilities: [
          { 
            id: "sw1", 
            name: "Outdated Operating Systems", 
            cve: "Various",
            description: "Devices running unsupported operating systems (e.g., Windows XP, Windows 7) with known vulnerabilities.",
            affectedDeviceTypes: ["imaging-systems", "laboratory-equipment", "older-medical-devices"],
            severity: "Critical",
            mitigation: "Isolate devices on separate networks, implement compensating controls, plan for device replacement."
          },
          { 
            id: "sw2", 
            name: "Lack of Update Mechanisms", 
            cve: "N/A",
            description: "Devices without a secure way to update firmware or software to address vulnerabilities.",
            affectedDeviceTypes: ["implantable-devices", "legacy-equipment"],
            severity: "High",
            mitigation: "Use network segmentation, implement monitoring, work with manufacturers on update processes."
          },
          { 
            id: "sw3", 
            name: "Buffer Overflows", 
            cve: "Various (e.g., CVE-2020-10633, CVE-2019-10966)",
            description: "Memory corruption vulnerabilities that can allow code execution or device crashes.",
            affectedDeviceTypes: ["patient-monitors", "imaging-systems", "infusion-pumps"],
            severity: "Critical",
            mitigation: "Apply vendor patches, implement network-based protections, use memory-safe programming languages."
          },
          { 
            id: "sw4", 
            name: "Third-Party Component Vulnerabilities", 
            cve: "Various",
            description: "Vulnerabilities in third-party libraries or components integrated into medical devices.",
            affectedDeviceTypes: ["all-connected-devices"],
            severity: "High",
            mitigation: "Track software bill of materials (SBOM), apply patches when available, monitor for vulnerability disclosures."
          }
        ]
      },
      {
        id: "network",
        name: "Network and Communication Issues",
        description: "Vulnerabilities in device network interfaces and protocols",
        vulnerabilities: [
          { 
            id: "net1", 
            name: "Exposed Services", 
            cve: "Various",
            description: "Unnecessary network services enabled on devices, expanding attack surface.",
            affectedDeviceTypes: ["imaging-systems", "patient-monitors", "network-connected-devices"],
            severity: "High",
            mitigation: "Disable unnecessary services, implement firewall rules, use network segmentation."
          },
          { 
            id: "net2", 
            name: "Insecure Protocols", 
            cve: "Various",
            description: "Use of insecure network protocols (e.g., Telnet, FTP, unencrypted DICOM) for device communication.",
            affectedDeviceTypes: ["imaging-systems", "legacy-devices", "clinical-workstations"],
            severity: "High",
            mitigation: "Replace with secure alternatives (SSH, SFTP, TLS), implement protocol filtering."
          },
          { 
            id: "net3", 
            name: "Bluetooth Vulnerabilities", 
            cve: "Various (e.g., CVE-2019-9506)",
            description: "Weaknesses in Bluetooth implementations allowing unauthorized access or data interception.",
            affectedDeviceTypes: ["wearable-devices", "remote-monitoring", "patient-monitors"],
            severity: "Medium",
            mitigation: "Update firmware, use latest Bluetooth standards, implement pairing restrictions."
          },
          { 
            id: "net4", 
            name: "Wi-Fi Vulnerabilities", 
            cve: "Various",
            description: "Weaknesses in wireless security implementation (e.g., WEP, weak WPA).",
            affectedDeviceTypes: ["mobile-medical-devices", "infusion-pumps", "patient-monitors"],
            severity: "High",
            mitigation: "Implement WPA3, use strong pre-shared keys, implement wireless intrusion detection."
          }
        ]
      },
      {
        id: "physical",
        name: "Physical Security Issues",
        description: "Vulnerabilities related to physical device access",
        vulnerabilities: [
          { 
            id: "phy1", 
            name: "Exposed USB Ports", 
            cve: "N/A",
            description: "Unrestricted physical ports allowing unauthorized firmware updates or data extraction.",
            affectedDeviceTypes: ["patient-monitors", "imaging-systems", "clinical-workstations"],
            severity: "Medium",
            mitigation: "Disable or restrict USB ports, use port blockers, implement USB device whitelisting."
          },
          { 
            id: "phy2", 
            name: "Unrestricted Physical Access", 
            cve: "N/A",
            description: "Lack of physical controls allowing unauthorized users to access device interfaces.",
            affectedDeviceTypes: ["all-medical-devices"],
            severity: "Medium",
            mitigation: "Implement physical access controls, device locks, and tamper-evident mechanisms."
          },
          { 
            id: "phy3", 
            name: "Maintenance Mode Access", 
            cve: "Various",
            description: "Insecure service or maintenance modes accessible without proper authentication.",
            affectedDeviceTypes: ["imaging-systems", "surgical-robots", "laboratory-equipment"],
            severity: "High",
            mitigation: "Implement strong authentication for maintenance modes, audit maintenance access."
          }
        ]
      },
      {
        id: "configuration",
        name: "Configuration and Deployment Issues",
        description: "Vulnerabilities related to device setup and management",
        vulnerabilities: [
          { 
            id: "conf1", 
            name: "Insecure Default Settings", 
            cve: "N/A",
            description: "Factory default configurations that prioritize usability over security.",
            affectedDeviceTypes: ["all-medical-devices"],
            severity: "High",
            mitigation: "Develop and follow secure configuration standards, verify settings before deployment."
          },
          { 
            id: "conf2", 
            name: "Excessive Privileges", 
            cve: "Various",
            description: "Systems or users granted more access rights than necessary for operation.",
            affectedDeviceTypes: ["clinical-information-systems", "connected-devices"],
            severity: "Medium",
            mitigation: "Implement principle of least privilege, regularly review access controls."
          },
          { 
            id: "conf3", 
            name: "Lack of Logging/Auditing", 
            cve: "N/A",
            description: "Insufficient event logging to detect or investigate security incidents.",
            affectedDeviceTypes: ["all-medical-devices"],
            severity: "Medium",
            mitigation: "Enable comprehensive logging, implement log monitoring, configure alerts for suspicious activity."
          },
          { 
            id: "conf4", 
            name: "No Network Segmentation", 
            cve: "N/A",
            description: "Medical devices placed on general-purpose networks without isolation.",
            affectedDeviceTypes: ["all-connected-devices"],
            severity: "High",
            mitigation: "Implement network segmentation, use VLANs and firewalls to isolate medical devices."
          }
        ]
      },
      {
        id: "clinical",
        name: "Clinical and Operational Impact",
        description: "Vulnerabilities with direct impact on patient care",
        vulnerabilities: [
          { 
            id: "clin1", 
            name: "Tampering with Clinical Data", 
            cve: "Various",
            description: "Vulnerabilities allowing modification of patient data affecting clinical decisions.",
            affectedDeviceTypes: ["diagnostic-equipment", "patient-monitors", "drug-delivery-systems"],
            severity: "Critical",
            mitigation: "Implement data integrity controls, digital signatures, access controls, and audit trails."
          },
          { 
            id: "clin2", 
            name: "Denial of Service to Critical Functions", 
            cve: "Various",
            description: "Vulnerabilities allowing attackers to disable or degrade device functionality during patient care.",
            affectedDeviceTypes: ["ventilators", "infusion-pumps", "life-support-systems"],
            severity: "Critical",
            mitigation: "Implement failsafe modes, network protections, backup systems, and continuity plans."
          },
          { 
            id: "clin3", 
            name: "Ransomware Susceptibility", 
            cve: "Various",
            description: "Vulnerability to ransomware attacks that can render devices or systems inoperable.",
            affectedDeviceTypes: ["imaging-systems", "clinical-workstations", "connected-medical-devices"],
            severity: "Critical",
            mitigation: "Implement backup systems, network segmentation, endpoint protection, and incident response plans."
          }
        ]
      },
      {
        id: "ai-ml-security",
        name: "AI/ML Security Issues",
        description: "Emerging vulnerabilities specific to AI-powered medical devices and clinical decision support systems",
        vulnerabilities: [
          {
            id: "ai1",
            name: "Adversarial Input Attacks on AI Diagnostics",
            cve: "Various (emerging class — no single CVE)",
            description: "Subtly manipulated medical images or data inputs designed to cause AI/ML diagnostic models to produce incorrect outputs (e.g., hiding a tumor from AI cancer screening or triggering false positives), without detectable differences to human reviewers.",
            affectedDeviceTypes: ["ai-clinical-decision-support", "diagnostic-imaging", "ai-radiology-analysis", "ai-pathology-analyzer"],
            severity: "Critical",
            mitigation: "Implement model robustness testing, adversarial training, human-in-the-loop review for high-stakes decisions, and input validation/anomaly detection."
          },
          {
            id: "ai2",
            name: "AI Model Poisoning via Training Data",
            cve: "N/A",
            description: "Attackers corrupt AI model training datasets or fine-tuning pipelines, causing the deployed model to produce systematically biased or dangerous outputs. Particularly relevant for federated learning models trained across multiple hospital systems.",
            affectedDeviceTypes: ["ai-clinical-decision-support", "ai-radiology-analysis"],
            severity: "High",
            mitigation: "Audit and validate training datasets, implement provenance tracking, use differential privacy techniques, and monitor model behavior in production for drift or anomalies."
          },
          {
            id: "ai3",
            name: "AI Supply Chain / Third-Party Model Risk",
            cve: "Various",
            description: "Healthcare organizations deploying AI models from third-party vendors may inherit vulnerabilities, backdoors, or biases embedded during model development. The FDA now requires SBOMs for cyber devices but AI model components remain difficult to audit.",
            affectedDeviceTypes: ["ai-clinical-decision-support", "ambient-clinical-ai", "ai-radiology-analysis"],
            severity: "High",
            mitigation: "Request AI model documentation and SBOM from vendors, perform due diligence on AI development practices, implement runtime monitoring, and include AI-specific security requirements in vendor contracts."
          },
          {
            id: "ai4",
            name: "Sensitive Data Exposure via AI Inference",
            cve: "N/A",
            description: "AI models can inadvertently expose training data through membership inference attacks or model inversion, allowing attackers to reconstruct patient data used in training. AI documentation systems (ambient clinical AI) create large repositories of sensitive audio and text PHI.",
            affectedDeviceTypes: ["ambient-clinical-ai", "ai-clinical-decision-support"],
            severity: "High",
            mitigation: "Apply differential privacy, limit data retention, encrypt all AI-related data at rest and in transit, and implement strict access controls on AI training and inference infrastructure."
          }
        ]
      }
    ],
    recentThreats: [
      {
        id: "recent1",
        name: "APT-Based Targeting of Healthcare",
        description: "Advanced Persistent Threat (APT) groups specifically targeting healthcare organizations for data theft, espionage, and disruption. In 2025, healthcare experienced more cyber threats than any other critical infrastructure sector.",
        relevance: "93% of healthcare organizations have confirmed known exploited vulnerabilities (KEVs) in their IoMT devices. 53% of networked medical devices have at least one known critical vulnerability (FBI Cyber Division, 2025).",
        mitigationStrategy: "Implement defense-in-depth strategies, threat intelligence sharing, continuous IoMT asset monitoring, and advanced endpoint protection. Prioritize patching known exploited vulnerabilities (KEVs)."
      },
      {
        id: "recent2",
        name: "Healthcare Ransomware Surge (2025)",
        description: "Healthcare ransomware attacks surged 30% in 2025, with attackers increasingly targeting vendors and third-party service partners to achieve simultaneous multi-facility impact. 77% of healthcare organizations were targeted; 53% paid the ransom. A new trend: extortion-only attacks (data exfiltration without encryption) tripled to 12% of incidents.",
        relevance: "Over a third of organizations experienced ransomware specifically designed to disrupt medical device operations. Supply chain compromise now accounts for 26% of incidents affecting multiple facilities simultaneously.",
        mitigationStrategy: "Implement immutable offline backups, network segmentation isolating medical devices, zero-trust architecture, and vendor/third-party risk management programs. Test incident response and recovery plans to meet the proposed 72-hour HIPAA recovery requirement."
      },
      {
        id: "recent3",
        name: "Supply Chain & Vendor Partner Attacks",
        description: "Attackers are shifting focus from direct hospital compromise to targeting medical device manufacturers, software vendors, and managed service providers — achieving broader reach through a single point of compromise. The FDA's mandatory SBOM requirement (2025 final guidance) is a direct response to this trend.",
        relevance: "26% of 2025 healthcare ransomware incidents involved supply chain compromises affecting multiple facilities. Third-party software components are the most common attack vector for medical device firmware.",
        mitigationStrategy: "Demand SBOMs from all medical device vendors, implement third-party risk assessments, monitor for vendor security advisories, verify software integrity via cryptographic signing, and establish contractual security obligations with vendors."
      },
      {
        id: "recent4",
        name: "AI/ML Adversarial Attacks on Medical Devices",
        description: "As AI-powered diagnostic and clinical decision support systems proliferate (1,200+ FDA-cleared AI/ML devices by mid-2025), adversarial attacks targeting AI model inputs have emerged as a new threat class. Subtly manipulated medical images can cause AI diagnostic systems to produce incorrect clinical outputs without being detectable by human reviewers.",
        relevance: "AI-powered medical devices are now deployed in radiology, pathology, cardiology monitoring, and clinical documentation. Compromising AI decision support can affect patient safety without triggering traditional security alerts.",
        mitigationStrategy: "Implement human-in-the-loop review for high-stakes AI-driven decisions, monitor AI model outputs for anomalous patterns, require vendor disclosure of model robustness testing, and include AI-specific security provisions in device procurement."
      },
      {
        id: "recent5",
        name: "Regulatory Deadline: November 2026 (HIPAA Final Rule + FDA QMSR)",
        description: "Three converging regulatory deadlines are creating acute compliance pressure: (1) HIPAA Security Rule final rule expected May 2026 — 180-day compliance window → November 2026 deadline. Mandatory: MFA for all remote ePHI access, AES-256 encryption at rest, TLS 1.2+ in transit, quarterly vulnerability scanning, ePHI asset inventory, BA 24-hour breach notification. (2) FDA QMSR effective February 2, 2026 — cybersecurity now embedded in ISO 13485 quality system obligations. (3) FDA revised premarket guidance February 9, 2026 — SPDF and SBOM requirements reaffirmed under QMSR framework.",
        relevance: "November 2026 is a hard compliance target for all HIPAA-covered entities and business associates. Organizations without MFA roadmaps, encryption implementations, and quarterly scanning programs in place now have fewer than 7 months to comply after expected final rule publication.",
        mitigationStrategy: "Start now: (1) Build ePHI asset inventory. (2) Map all ePHI systems to MFA readiness — identify devices requiring compensating controls. (3) Verify AES-256/TLS 1.2+ compliance for all ePHI storage and transmission. (4) Establish quarterly vulnerability scanning schedule. (5) Update BAAs to require 24-hour breach notification. (6) Request SBOMs from all device vendors and set up vulnerability notification intake processes."
      }
    ]
  };